import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/authContext';
import useHttp from './useHttp';

// Loads and toggles A/B/n experiments / feature flags, and loads their results.
// The catalog of experiments lives in the backend; here we read their merged
// status, flip enabled / per-variant weights, and pull per-variant metrics.
const useExperiments = () => {
  const { sendRequest, isLoading } = useHttp();
  const { getAuthHeaders } = useAuth();
  const [experiments, setExperiments] = useState([]);

  const fetchExperiments = useCallback(async () => {
    try {
      const data = await sendRequest({
        url: '/api/v1/experiments',
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (data?.experiments) setExperiments(data.experiments);
      return data;
    } catch (err) {
      toast.error('Fehler beim Laden der A/B-Tests');
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  const updateExperiment = useCallback(
    async (key, patch) => {
      try {
        const data = await sendRequest(
          {
            url: `/api/v1/experiments/${encodeURIComponent(key)}`,
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: patch,
          },
          'Fehler beim Speichern des A/B-Tests'
        );
        if (data?.experiment) {
          setExperiments(prev => prev.map(e => (e.key === key ? data.experiment : e)));
        }
        return data?.experiment;
      } catch (err) {
        toast.error('Fehler beim Speichern des A/B-Tests');
        throw err;
      }
    },
    [sendRequest, getAuthHeaders]
  );

  const fetchResults = useCallback(
    async (key, { startDate, endDate, widgetId } = {}) => {
      try {
        const data = await sendRequest({
          url: `/api/v1/experiments/${encodeURIComponent(key)}/results`,
          method: 'GET',
          headers: getAuthHeaders(),
          params: { startDate, endDate, widgetId },
        });
        return data;
      } catch (err) {
        toast.error('Fehler beim Laden der Test-Ergebnisse');
        throw err;
      }
    },
    [sendRequest, getAuthHeaders]
  );

  return { experiments, isLoading, fetchExperiments, updateExperiment, fetchResults };
};

export default useExperiments;
